import type {ReactNode, RefObject} from "react";
import {
	Option, OptionAside, OptionChip, OptionDetail, OptionMain, OptionTitle, OptionTitleRow, PlanChip, Radio,
	RadioDot,
} from "./OfferOption.styles";

/**
 * One selectable offer row: a radio at the left, a title row, and an optional smaller second line
 * (kitecorp/transferify-ui#1722). Written for the exit offer's terms, and shared by the
 * first-month dialog's three tiers (kitecorp/transferify-ui#2948), so both dialogs select the same way.
 *
 * A real `button` carrying `role="radio"`, so the whole row is the target (44px-plus at every width)
 * and Enter and Space select it for free. The two halves a `role` does NOT provide are the roving tab
 * stop, written here from {@link ./radioGroup.radioTabStop}, and arrow-key movement, which is
 * {@link ./useRadioGroupKeys} on the group.
 */
export function OfferOption(
	{selected, tabStop = selected, onSelect, selectedRef, testId, title, chip, aside, detail, plan}: {
		selected: boolean;
		/**
		 * Whether this row holds the group's one tab stop, which {@link ./radioGroup.radioTabStop} answers: the
		 * checked row, or the FIRST row while nothing is checked (kitecorp/transferify-ui#3229). Defaults to
		 * `selected`, which is the same answer for every group that always has a choice.
		 */
		tabStop?: boolean;
		onSelect: () => void;
		selectedRef: RefObject<HTMLButtonElement | null>;
		testId: string;
		title: string;
		chip?: string;
		/** A figure at the end of the title row: the first-month dialog's price. */
		aside?: ReactNode;
		/** Absent on the intro option since ui#2426: no struck-through list price sits under a reduced one. */
		detail?: ReactNode;
		/** The plan the row sells, named at its right end in that plan's accent (ui#2994). */
		plan?: {tier: "premium" | "plus"; label: string};
	},
) {
	return (
		<Option
			type="button"
			role="radio"
			aria-checked={selected}
			// The roving tab stop: only one row is in the tab order, so Tab enters and leaves the GROUP rather
			// than stopping on each term, which is the pattern's rule. With nothing checked it is the first row,
			// or the group could not be reached with Tab at all.
			tabIndex={tabStop ? 0 : -1}
			ref={tabStop ? selectedRef : undefined}
			$selected={selected}
			onClick={onSelect}
			data-testid={testId}
		>
			{/* Decorative: `aria-checked` on the row already carries the state this dot draws. */}
			<Radio $selected={selected} aria-hidden="true">{selected && <RadioDot/>}</Radio>
			<OptionMain>
				<OptionTitleRow>
					<OptionTitle>{title}</OptionTitle>
					{chip !== undefined && <OptionChip>{chip}</OptionChip>}
					{aside !== undefined && <OptionAside>{aside}</OptionAside>}
				</OptionTitleRow>
				{detail !== undefined && <OptionDetail>{detail}</OptionDetail>}
			</OptionMain>
			{plan !== undefined && <PlanChip $plan={plan.tier}>{plan.label}</PlanChip>}
		</Option>
	);
}
